import { Autocomplete, Box, CircularProgress, Radio, TextField, ClickAwayListener } from "@mui/material";
import { DataGridProProps, GridActionsCellItem, GridColDef, GridPaginationModel } from "@mui/x-data-grid-pro";
import { Ico_RoundedPlus, Ico_Delete, CentralDataGridPro, Ico_MenuDots, Ico_SortAscending, Ico_SortDescending, Ico_Search, Ico_Close } from "@sagecentral/core-ui";
import { TextFilterOperator, TaskProblemFilterOperator } from "components/shared/filters";
import { GridBox, GridNoRowsOverlay } from "components/shared/grids";
import CustomGridTreeDataGroupingCell from "./CustomGridTreeDataGroupingCell";
import { useEffect, useState } from "react";
import useAddResourceToResponsibility from "hooks/work-items/useAddResourceToResponsibility";
import useChangePrimaryWorkItemResourceAllocation from "hooks/work-items/useChangePrimaryWorkItemResourceAllocation";
import useWorkItemGetResourcesByResponsibility from "hooks/work-items/useWorkItemGetResourcesByResponsibility";

const NoRowsOverlay = () => {
    return (
        <GridNoRowsOverlay text={`No responsibilities found.`} />
    );
};

const ResourceAllocationGrid = (props: any) => {
    const {
        data,
        loading,
        workItem,
        workItemPermissions,
        draft,
        setResourceToDelete,
        setOpenDeleteConfirmation,
        setDataState,
        refreshData } = props;
    const [newResources, setNewResources] = useState<{ [key: string]: any[] }>({});
    const [dropdownOptions, setDropdownOptions] = useState<{ [key: string]: any[] }>({});
    const [showDropdownForResponsibility, setShowDropdownForResponsibility] = useState<{ [key: string]: boolean }>({});
    const [responsibilityMap, setResponsibilityMap] = useState<{ [key: string]: string }>({});
    const [responsibilityRequiredMap, setResponsibilityRequiredMap] = useState<{ [key: string]: boolean }>({});
    const [selectedPrimary, setSelectedPrimary] = useState<{ [key: string]: string | null }>({});
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });
    const [rowAlternation, setRowAlternation] = useState<Record<string, 'row odd' | 'row even'>>({});
    const handleAddResource = useAddResourceToResponsibility();
    const handleChangePrimaryResource = useChangePrimaryWorkItemResourceAllocation();

    const getWorkItemResourcesByResponsibility = useWorkItemGetResourcesByResponsibility();
    const handlePrimaryChange = async (responsibility: string, resourceId: string, workItemId: number, resourceAllocationId: number) => {
        setSelectedPrimary((prev) => ({
            ...prev,
            [responsibility]: resourceId,
        }));

        await handleChangePrimaryResource(workItemId, resourceAllocationId, resourceId, draft);
        refreshData();
    };

    useEffect(() => {
        if (data) {
            const map: { [key: string]: string } = {};
            const responsibilityRequiredMap: { [key: string]: boolean } = {};
            const initialPrimaryState: { [key: string]: string | null } = {};

            data.forEach((row: any) => {
                if (row.responsibilityName) {
                    map[row.responsibilityName] = row.responsibilityId;
                }

                if (row.responsibilityRequired) {
                    responsibilityRequiredMap[row.responsibilityName] = true;
                }

                if (row.primary) {
                    initialPrimaryState[row.responsibilityName] = row.resourceId;
                }
            });

            const responsibilityOrder: string[] = Array.from(
                new Set(data.map((row: any) => row.responsibilityName))
            );

            const alternationMap = responsibilityOrder.reduce((acc, responsibility, index) => {
                acc[responsibility] = index % 2 === 0 ? 'row odd' : 'row even';
                return acc;
            }, {} as Record<string, 'row odd' | 'row even'>);

            setRowAlternation(alternationMap);


            setResponsibilityMap(map);
            setResponsibilityRequiredMap(responsibilityRequiredMap);
            setSelectedPrimary(initialPrimaryState);
        }
    }, [data]);

    const handleAddResourceClick = async (params: { row: { responsibilityName: string }; rowNode: { depth: number; groupingField?: string } }) => {
        const responsibilityName: string = params.row.responsibilityName || '';
        const responsibilityId: number = Number(responsibilityMap[responsibilityName]);
    
        const alreadyHasResources = data?.some(
            (r : any) => r.responsibilityName === responsibilityName && r.resourceId
        );
        
        if (dropdownOptions[responsibilityName]) {
            setShowDropdownForResponsibility((prev) => ({
                ...prev,
                [responsibilityName]: true,
            }));
    
            if (!alreadyHasResources) {
                return;
            }

            if (!newResources[responsibilityName]?.length) {
                setNewResources((prev) => ({
                    ...prev,
                    [responsibilityName]: [
                        {
                            id: `new-${Date.now()}`,
                            resourceId: '',
                            resourceName: '',
                            responsibilityName: responsibilityName,
                            responsibilityId: responsibilityId,
                            tempId: Math.random().toString(36).substr(2, 9),
                            treePath: [responsibilityName, ""]
                        }
                    ]
                }));
            }
    
            return;
        }
    
        const resources = await getWorkItemResourcesByResponsibility({
            workspaceId: workItem?.workspaceId ?? 0,
            responsibilityId,
            workItemId: workItem?.id ?? 0,
            draft
        });
    
        setDropdownOptions((prev) => ({
            ...prev,
            [responsibilityName]: resources,
        }));
    
        setShowDropdownForResponsibility((prev) => ({
            ...prev,
            [responsibilityName]: true,
        }));
    
        if (alreadyHasResources) {
            setNewResources((prev) => ({
                ...prev,
                [responsibilityName]: [
                    {
                        id: `new-${Date.now()}`,
                        resourceId: '',
                        resourceName: '',
                        responsibilityName: responsibilityName,
                        responsibilityId: responsibilityId,
                        tempId: Math.random().toString(36).substr(2, 9),
                        treePath: [responsibilityName, ""]
                    }
                ]
            }));
        }
    };

    const handleDeleteResourceClick = async (workItemId: number, resourceAllocationId: number) => {
        setResourceToDelete({ workItemId, resourceAllocationId });

        setOpenDeleteConfirmation(true);
    }


    const columns: GridColDef[] = [

        {
            field: 'primary', headerName: 'Primary',
            headerClassName: 'header',
            type: 'boolean',
            minWidth: 120, width: 150, sortable: true, resizable: true, hideable: true, hideSortIcons: false, filterOperators: TaskProblemFilterOperator,
            renderCell: (params) => {
                const { rowNode, row } = params;
                const isSelectedPrimary = selectedPrimary[row.responsibilityName] === row.resourceId;

                return row.resourceId ? (
                    <Radio
                        sx={{mr: '35px'}}
                        disabled={!workItemPermissions?.canChangeWorkItemResources}
                        checked={isSelectedPrimary}
                        onChange={() => handlePrimaryChange(row.responsibilityName, row.resourceId, row.workItemId, row.id)}
                        value={row.resourceId}
                        name={`primary-radio-button-${row.responsibilityName}`}
                        inputProps={{
                            'aria-label': `Set ${row.displayName || row.resourceName} as primary resource for ${row.responsibilityName}`,
                            'aria-checked': isSelectedPrimary,
                            'tabIndex': 0
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handlePrimaryChange(row.responsibilityName, row.resourceId, row.workItemId, row.id);
                            }
                        }}
                    />
                ) : null;
            }
        },
        {
            field: "resourceName",
            flex: 1,
            headerName: "Resource",
            headerClassName: "header",
            filterOperators: TextFilterOperator,
            sortable: true,
            hideSortIcons: false,
            resizable: true,
            width: 250,
            renderCell: (params) => {
                const { rowNode, row } = params;
                const resources = dropdownOptions[row.responsibilityName] || [];
                if (
                    (rowNode.depth === 0 &&
                        showDropdownForResponsibility[row.responsibilityName] &&
                        !row.resourceId) ||
                       (row.tempId && showDropdownForResponsibility[row.responsibilityName])
                ) {
                    return (
                        <ClickAwayListener onClickAway={() => {
                            const responsibilityName = row.responsibilityName;
                            setShowDropdownForResponsibility((prev) => ({
                                ...prev,
                                [responsibilityName]: false,
                            }));
                            
                            if (row.tempId) {
                                setNewResources((prev) => {
                                    const { [responsibilityName]: _, ...rest } = prev;
                                    return rest;
                                });
                            }
                        }}>
                            <Box 
                                sx={{ display: 'flex', alignItems: 'center', width: '100%', pt: '10px' }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape' && !e.defaultPrevented) {
                                        e.preventDefault();
                                        const responsibilityName = row.responsibilityName;
                                        setShowDropdownForResponsibility((prev) => ({
                                            ...prev,
                                            [responsibilityName]: false,
                                        }));
                                        
                                        if (row.tempId) {
                                            setNewResources((prev) => {
                                                const { [responsibilityName]: _, ...rest } = prev;
                                                return rest;
                                            });
                                        }
                                    }
                                }}
                            >
                                <Box sx={{ flexGrow: 1 }}>
                                    <Autocomplete
                                        sx={{
                                            "& .MuiAutocomplete-popupIndicator": {
                                                transform: "none !important",
                                                transition: "none !important",
                                            },
                                        }}
                                        fullWidth
                                        disableClearable
                                        options={resources}
                                        getOptionLabel={(option) => option.displayName}
                                        value={resources.find(resource => resource.resourceName === row.resourceName) || null}
                                        onKeyDown={(event) => {
                                            event.stopPropagation();
                                            if (event.key === 'Escape') {
                                                const responsibilityName = row.responsibilityName;
                                                setShowDropdownForResponsibility((prev) => ({
                                                    ...prev,
                                                    [responsibilityName]: false,
                                                }));
                                                
                                                if (row.tempId) {
                                                    setNewResources((prev) => {
                                                        const { [responsibilityName]: _, ...rest } = prev;
                                                        return rest;
                                                    });
                                                }
                                            }
                                        }}
                
                                        onChange={async (_, newValue) => {
                                            if (newValue) {
                                                const { resourceId, resourceName } = newValue;
                                                row.resourceName = resourceName;
                                                const resourceAllocationId = await handleAddResource(workItem?.id ?? 0, Number(row.responsibilityId), resourceId, draft);
            
                                                if (resourceAllocationId) {
                                                    const responsibilityName = params.row.responsibilityName;
            
                                                    setShowDropdownForResponsibility((prev) => ({
                                                        ...prev,
                                                        [responsibilityName]: false,
                                                    }));
            
                                                    setNewResources((prev) => ({
                                                        ...prev,
                                                        [responsibilityName]: [],
                                                    }));
                                                    setDropdownOptions((prev) => {
                                                        const { [responsibilityName]: _, ...rest } = prev;
                                                        return rest;
                                                    });
                                                    refreshData();
                                                } else {
                                                    const updatedData = data.filter((resource: any) => resource.tempId !== row.tempId);
                                                    setDataState(updatedData);
                                                }
                                            }
                                        }}
                                        popupIcon={<Ico_Search sx={{ width: '24px', height: '24px', transform: "none !important" }} aria-hidden="true" />}
                                        renderInput={(params) => <TextField 
                                            sx={{ 
                                                "& label": { top: "-5px" },
                                                "& input::placeholder": { fontSize: "15px" },
                                                "& .MuiInputBase-input": { fontSize: "15px" }
                                            }} 
                                            {...params} 
                                            placeholder="Search by resource or vendor" 
                                            variant="outlined"
                                            inputProps={{
                                                ...params.inputProps,
                                                'aria-label': `Search for resource to assign to ${row.responsibilityName}`,
                                                'aria-autocomplete': 'list'
                                            }}
                                        />}
                                        aria-label={`Select resource for ${row.responsibilityName}`}
                                        ListboxProps={{
                                            'aria-label': `Available resources for ${row.responsibilityName}`,
                                            'role': 'listbox'
                                        }}
                                    />
                                </Box>
                                <Box sx={{ ml: 1, display: 'block', alignItems: 'center', pb: '4%' }}>
                                    <Box 
                                        onClick={() => {
                                            const responsibilityName = row.responsibilityName;
                                            setShowDropdownForResponsibility((prev) => ({
                                                ...prev,
                                                [responsibilityName]: false,
                                            }));
                                            
                                            if (row.tempId) {
                                                setNewResources((prev) => {
                                                    const { [responsibilityName]: _, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
                                                e.preventDefault();
                                                const responsibilityName = row.responsibilityName;
                                                setShowDropdownForResponsibility((prev) => ({
                                                    ...prev,
                                                    [responsibilityName]: false,
                                                }));
                                                
                                                if (row.tempId) {
                                                    setNewResources((prev) => {
                                                        const { [responsibilityName]: _, ...rest } = prev;
                                                        return rest;
                                                    });
                                                }
                                            }
                                        }}
                                        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                        role="button"
                                        tabIndex={0}
                                        aria-label="Cancel resource selection"
                                    >
                                        <Ico_Close 
                                            color='error'
                                            sx={{
                                                height: '20px',
                                                width: '20px'
                                            }}
                                            aria-hidden="true"
                                        />
                                        <span className="visually-hidden">Cancel resource selection</span>
                                    </Box>
                                </Box>
                            </Box>
                        </ClickAwayListener>
                    );
                }

                return row.displayName;
            }

        },
        {
            field: "addResource", headerName: "Add Resource", headerClassName: "header", type: "actions",
            sortable: false, hideSortIcons: true, resizable: false, width: 150,
            renderCell: (params: any) => {
                if (workItemPermissions?.canChangeWorkItemResources) {
                    if (params.rowNode.depth === 0) {
                        return (
                            <GridActionsCellItem
                                icon={<Ico_RoundedPlus sx={{ width: '24px', height: '24px', color: "#001A69" }} />}
                                label="Add Resource"
                                onClick={() => handleAddResourceClick(params)}
                                aria-label="Add resource to responsibility"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleAddResourceClick(params);
                                    }
                                }}
                            />
                        );
                    }
                }
                return null;
            }
        },
        {
            field: 'remove', headerName: 'Remove', headerClassName: 'header',
            type: 'actions', sortable: false, hideSortIcons: true, resizable: false, width: 150,
            renderCell: (params) => {
                if (params.row.resourceId && params.row.canResourceBeDeleted) {
                    return <GridActionsCellItem
                        icon={<Ico_Delete sx={{ width: '24px', height: '24px', color: "#001A69" }} />}
                        label=""
                        onClick={() => handleDeleteResourceClick(params.row.workItemId, params.row.id)}
                        showInMenu
                        aria-label={`Remove ${params.row.displayName || params.row.resourceName} from ${params.row.responsibilityName}`}
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleDeleteResourceClick(params.row.workItemId, params.row.id);
                            }
                        }}
                    />
                }
            }
        },
    ];

    const getTreeDataPath = (row: any) => (row.treePath);


    const getData = () => {
        const combinedData = data ? [...data] : [];
        const groupedData: any[] = [];

        const responsibilityMap: { [key: string]: boolean } = {};
        combinedData.forEach((row) => {
            const { responsibilityName, resourceId } = row;

            if (!responsibilityMap[responsibilityName]) {
                groupedData.push({
                    ...row,
                    treePath: [responsibilityName],
                });
                responsibilityMap[responsibilityName] = true;
            } else {
                groupedData.push({
                    ...row,
                    treePath: [responsibilityName, resourceId],
                });
            }
        });

        Object.keys(newResources).forEach((responsibility) => {
            const newRows = newResources[responsibility] || [];

            if (!responsibilityMap[responsibility]) {
                groupedData.push({
                    responsibilityName: responsibility,
                    treePath: [responsibility],
                });
                responsibilityMap[responsibility] = true;
            }

            newRows.forEach((newRow) => {
                groupedData.push({
                    ...newRow,
                    treePath: [responsibility, newRow.resourceId],
                });
            });
        });

        return groupedData;
    };




    const groupingColDef: DataGridProProps['groupingColDef'] = {
        headerName: 'Responsibility',
        flex: 1,
        sortable: true,
        width: 300,
        renderCell: (params) => {
            const { rowNode, value, row } = params;

            if (rowNode.depth === 0) {
                const isRequired = responsibilityRequiredMap[value];
                return (
                    <Box sx={{ ml: rowNode.depth * 4 }}>
                        <span role="heading" aria-level={3}>{value}</span> 
                        {isRequired ? (
                            <Box 
                                sx={{display: 'inline', color: row.resourceId ? 'black' : '#dc3545' }}
                                aria-label={row.resourceId ? 
                                    `Required responsibility, has resources assigned` : 
                                    `Required responsibility, needs resources assigned`}
                            >
                                <i>(required)</i>
                            </Box>
                        ) : null}
                    </Box>
                );
            }

            return <CustomGridTreeDataGroupingCell {...params} />;
        },
    };

    const getRowClassName = (params: any) => {
        const { responsibilityName } = params.row;
        if (responsibilityName) {
            return rowAlternation[responsibilityName] || '';
        }
        return rowAlternation[params.id.split('/')[1]];
    };

    return (<>
        <style>{`
            .visually-hidden { 
                border: 0;
                clip: rect(0 0 0 0);
                height: 1px;
                margin: -1px;
                overflow: hidden;
                padding: 0;
                position: absolute;
                width: 1px;
            }
        `}</style>
        <GridBox getData={getData}>
            <Box sx={{ height: "80vh" }}>
                <div id="resource-allocation-grid-description" className="visually-hidden">
                    Resource allocation grid for this work item. Use arrow keys to navigate, Enter or Space to interact with controls.
                </div>
                <CentralDataGridPro
                    aria-describedby="resource-allocation-grid-description"
                    rows={getData()}
                    getRowId={(r) => r.id || `${r.responsibilityName}-${r.resourceId}`}
                    columns={columns}
                    treeData
                    getTreeDataPath={getTreeDataPath}
                    groupingColDef={groupingColDef}
                    defaultGroupingExpansionDepth={-1}
                    filterMode="client"
                    sortingMode="client"
                    pagination
                    paginationMode="client"
                    rowSelection={false}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => setPaginationModel(model)}
                    disableDensitySelector={true}
                    slots={{
                        loadingOverlay: () => (<CircularProgress />),
                        columnMenuSortAscendingIcon: Ico_SortAscending,
                        columnMenuSortDescendingIcon: Ico_SortDescending,
                        columnSortedAscendingIcon: Ico_SortAscending,
                        columnSortedDescendingIcon: Ico_SortDescending,
                        columnUnsortedIcon: undefined,
                        moreActionsIcon: Ico_MenuDots,
                        noRowsOverlay: () => NoRowsOverlay(),
                    }}
                    loading={loading || !data}
                    isRowSelectable={(params) => !!params.row.resourceId}
                    getRowClassName={getRowClassName}
                />
            </Box>

        </GridBox>
    </>)
}

export default ResourceAllocationGrid;